// src/pages/departments/ChemicalEngineering.js

import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Award,
  BadgeCheck,
  Beaker,
  BookOpen,
  BookOpenCheck,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Factory,
  FileText,
  FlaskConical,
  GraduationCap,
  Leaf,
  Mail,
  MapPin,
  Maximize2,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
  Users2,
  Wrench,
  X,
} from "lucide-react";

/* =========================================================
   SMALL HELPERS
========================================================= */

const CHEMICAL_IMAGE_BASE = "/images/departments/chemical-engineering";
const CHEMICAL_STAFF_IMAGE_BASE = `${CHEMICAL_IMAGE_BASE}/staff`;

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.75, delay }}
    viewport={{ once: true, amount: 0.1 }}
    className="origin-left h-1 bg-gradient-to-r from-blue-700 to-blue-400 rounded-full my-8"
  />
);

/* =========================================================
   STAFF DATA

   The source CSV contained duplicate submissions for:
   - Abubakar Sadiq Umar
   - Usman Shaba Mohammed

   Each duplicate is represented once here using the richer/latest
   submitted academic details.
========================================================= */

const staffList = [
  {
    id: 1,
    title: "Engr. Dr.",
    name: "Muhammad Adamu",
    role: "Head of Department • Chief Lecturer",
    rank: "Chief Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/muhammad-adamu.jpg`,
    qualifications: ["B.Eng, M.Eng, PhD (FUT Minna)"],
    professionalQualifications: ["MNSChE", "MNSE", "COREN"],
    publications: "31 journal publications",
    researchInterests: [
      "Catalysis",
      "Biofuels",
      "Biomass Technology",
      "Petroleum Engineering",
      "Oil and Gas Processing Technology",
      "Modelling and Simulation",
      "Process Design and Development",
    ],
    specialties: [
      "B.Eng (Chemical Engineering)",
      "M.Sc (Modelling and Simulation)",
      "PhD (Chemical Engineering / Biofuels & Catalysis)",
    ],
    awards: [
      "Two Institution-Based Research (IBR) grant awards",
      "Design and fabrication of a steam distillation plant for essential oils production",
      "Design and fabrication of a mini water treatment plant",
      "Development of a bi-functional catalyst for simultaneous esterification and transesterification of vegetable oils to biodiesel",
    ],
    email: "Mohammed.adamu@fedpolybida.edu.ng",
    phone: "08065295882",
    featured: true,
    badge: "HOD",
  },
  {
    id: 2,
    title: "Engr.",
    name: "David Gana Tsado",
    role: "Chief Lecturer",
    rank: "Chief Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/david.jpeg`,
    qualifications: [
      "B.Eng (FutMin)",
      "M.Sc (UniSurrey, UK)",
    ],
    professionalQualifications: ["MNSE", "MNSChE", "COREN"],
    publications: "7 outstanding academic publications",
    researchInterests: [
    
      "Process Systems Engineering",
      "Environmental Systems Engineering",
      "Energy and Process Systems Engineering",
      "Energy System Integration",
      "Life Cycle Thinking and Sustainable Energy Production",
      "Nanoparticle Doped Na ion Battery Production",
      "Biomass Technology",
    ],
    specialties: [
      "B.Eng (Chemical Engineering)",
      "M.Sc (Process & Environmental Systems Engineering)",
    ],
    
    email: "tsado.david.g@fedpolybida.edu.ng",
    phone: "+2348034111893",
    badge: "Chief Lecturer",
  },
  {
    id: 3,
    title: "Engr. Dr.",
    name: "Abdullahi Mohammed",
    role: "Chief Lecturer",
    rank: "Chief Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/abdullahi-mohammed.jpg`,
    qualifications: [
      "HND (Federal Polytechnic Bida)",
      "PGD (FUT Minna)",
      "PGDE (ABU Zaria)",
      "B.Sc (KWASU)",
      "M.Eng (FUT Minna)",
      "PhD (FUT Minna)",
    ],
    professionalQualifications: ["MNSE", "MNSChE", "MCSN", "MNATE", "COREN"],
    publications: "31 outstanding academic publications",
    researchInterests: [
      "Environmental Technology",
      "Mathematical Modelling and Simulation",
      "Carbon Sequestration",
      "Analysis of Silicate Materials",
      "Finite Element Analysis and Multiphysics Simulation",
    ],
    specialties: [
      "PGD (Chemical Engineering)",
      "B.Sc (Industrial Chemistry)",
      "M.Eng (Chemical Engineering)",
      "PhD (Chemical Engineering)",
    ],
    awards: [
      "TETFUND National Research Fund (NRF) Grant",
      "Institution-Based Research (IBR) Grant, Federal Polytechnic Bida",
      "TETFUND 2024 Fabrication Intervention",
      "Design and fabrication of a biomass-fired dryer for agricultural products — TETFUND 2025 Fabrication Intervention",
    ],
    email: "dr.abdullahi@fedpolybida.edu.ng",
    phone: "08036615973",
    badge: "Chief Lecturer",
  },
  {
    id: 4,
    title: "Engr.",
    name: "Shafihi Umar",
    role: "Chief Lecturer",
    rank: "Chief Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/shafihi-umar.jpg`,
    qualifications: ["B.Eng (FUT Minna)", "M.Sc (UPM Malaysia)"],
    professionalQualifications: ["MNSE", "COREN", "MNSChE"],
    publications: "9 outstanding academic publications",
    researchInterests: [
      "Sustainable Chemical and Environmental Process Engineering",
      "Environmental Modelling, Simulation and Process Optimization",
      "Waste-to-Energy and Resource Recovery",
    ],
    specialties: [
      "B.Eng Chemical Engineering",
      "M.Sc Environmental Engineering",
      "PhD in progress",
    ],
    awards: [],
    email: "umar.shafihi@fedpolybida.edu.ng",
    phone: "08065423222",
    badge: "Chief Lecturer",
  },
  {
    id: 5,
    title: "Engr.",
    name: "Anina Yawo Alfa",
    role: "Principal Lecturer",
    rank: "Principal Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/anina-yawo-alfa.jpg`,
    qualifications: ["B.Sc, M.Sc (FUT Minna)"],
    professionalQualifications: ["MNSChE", "MNSE", "COREN"],
    publications: "",
    researchInterests: ["Renewable Energy", "Process Engineering"],
    specialties: ["B.Eng (Chemical Engineering)", "M.Sc (Chemical Engineering)"],
    awards: [],
    email: "aminaalfa@fedpolybida.edu.ng",
    phone: "08034505180",
    badge: "Principal Lecturer",
  },
  {
    id: 6,
    title: "Engr.",
    name: "Haruna Muhammad",
    role: "Principal Lecturer",
    rank: "Principal Lecturer",
    image:`${CHEMICAL_STAFF_IMAGE_BASE}/haru.jpeg`,
    qualifications: ["B.Tech (FUT Minna)", "M.Sc (ABU Zaria)"],
    professionalQualifications: ["NSChE", "MNSE", "COREN"],
    publications: "Seventeen outstanding academic publications",
    researchInterests: ["Biofuel", "Renewable Energy", "Waste Management", "Production"],
    specialties: ["B.Tech (Chemical Engineering)", "M.Sc Biofuel"],
    awards: [],
    email: "haruna.mohamed@fedpolybida.edu.ng",
    phone: "08039724055",
    badge: "Principal Lecturer",
  },
  {
    id: 7,
    title: "Engr.",
    name: "Areegbe Oladapo Adetunji",
    role: "Senior Lecturer",
    rank: "Senior Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/areegbe-oladapo-adetunji.jpg`,
    qualifications: ["PGD (FUT Minna)"],
    professionalQualifications: ["MNSE"],
    publications: "4 outstanding academic publications",
    researchInterests: ["Polymer Science", "Absorption"],
    specialties: ["PGD (Chemical Engineering)"],
    awards: [],
    email: "areegbe@fedpolybida.edu.ng",
    phone: "07035373306",
    badge: "Senior Lecturer",
  },
  {
    id: 8,
    title: "Engr.",
    name: "Dokochi Mohammed Abdulkadir",
    role: "Senior Lecturer",
    rank: "Senior Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/dokochi-mohammed-abdulkadir.jpg`,
    qualifications: ["M.Eng (FUT Minna)"],
    professionalQualifications: ["NSChE", "MNSE", "COREN"],
    publications: "4 outstanding academic publications",
    researchInterests: ["Renewable Energy", "Process Optimization", "Environmental Engineering"],
    specialties: [
      "B.Eng (Chemical Engineering)",
      "M.Eng (Process Engineering)",
      "PhD in view (Chemical Engineering)",
    ],
    awards: ["Renewable Energy"],
    email: "dokochi.abdulkadir@fedpolybida.edu.ng",
    phone: "08032833289",
    badge: "Senior Lecturer",
  },
  {
    id: 9,
    title: "Engr.",
    name: "Abubakar Mustapha Nma",
    role: "Lecturer III",
    rank: "Lecturer III",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/abubakar-mustapha-nma.jpg`,
    qualifications: ["B.Sc (FUT Minna)", "M.Sc (ABU Zaria)"],
    professionalQualifications: ["MNSChE", "MNSE", "COREN", "MIAENG"],
    publications: "",
    researchInterests: ["Materials", "Polymer"],
    specialties: ["B.Eng (Chemical Engineering)", "M.Sc (Chemical Engineering)"],
    awards: [],
    email: "mustapha.nmaabubakar@fedpolybida.edu.ng",
    phone: "08036419504",
    badge: "Lecturer III",
  },
  {
    id: 10,
    title: "Engr.",
    name: "Lawrencia Ijeoma Ukaobasi",
    role: "Lecturer III",
    rank: "Lecturer III",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/lawrencia-ijeoma-ukaobasi.jpeg`,
    qualifications: ["B.Eng (FUT Owerri)", "M.Sc (NAU Awka — in view)"],
    professionalQualifications: ["COREN", "MNSE", "MNSChE", "MNISP", "MNIFST"],
    publications: "5 outstanding academic publications",
    researchInterests: [
      "Food Processing and Storage Technology",
      "Postharvest Technology",
      "Smart Packaging and Materials",
    ],
    specialties: [
      "B.Eng (Chemical Engineering)",
      "M.Sc (Food Processing and Storage Technology)",
    ],
    awards: [],
    email: "ijeoma.lawrenciaibegbule@fedpolybida.edu.ng",
    phone: "09079449622",
    badge: "Lecturer III",
  },
  {
    id: 11,
    title: "Engr.",
    name: "Manmasun Ndagi Abubakar",
    role: "Lecturer III",
    rank: "Lecturer III",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/manmasun-ndagi-abubakar.jpg`,
    qualifications: [
      "ND",
      "HND (Federal Polytechnic Bida)",
      "PGDE (UDUS)",
      "B.Eng (FUT Minna)",
      "M.Eng (FUT Minna)",
    ],
    professionalQualifications: ["TRCN", "COREN"],
    publications: "",
    researchInterests: ["Renewable Energy", "Energy and Sustainability"],
    specialties: ["B.Eng Chemical Engineering", "M.Eng Chemical Engineering"],
    awards: [],
    email: "manmasun.abubakar@fedpolybida.edu.ng",
    phone: "08036102451",
    badge: "Lecturer III",
  },
  {
    id: 12,
    title: "Mr.",
    name: "Oghenejivwe Dominion",
    role: "Lecturer III",
    rank: "Lecturer III",
    image: "/images/default-staff.jpg",
    qualifications: ["M.Eng"],
    professionalQualifications: [],
    publications: "",
    researchInterests: ["Natural Gas", "Drilling Engineering", "Production Engineering"],
    specialties: ["B.Eng (Petroleum and Gas Engineering)"],
    awards: [],
    email: "oghenejivwe.dominion@fedpolybida.edu.ng",
    phone: "07034900122",
    badge: "Lecturer III",
  },
  {
    id: 13,
    title: "Engr.",
    name: "Aliyu Sani",
    role: "Assistant Lecturer",
    rank: "Assistant Lecturer",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/aliyu-sani.jpg`,
    qualifications: ["B.Eng (ABU Zaria)", "M.Eng (FUT Minna)"],
    professionalQualifications: ["MNSE"],
    publications: "",
    researchInterests: ["Nanotechnology", "Remediation of Wastewater"],
    specialties: ["B.Eng Chemical Engineering", "M.Eng Chemical Engineering"],
    awards: [],
    email: "aliyusani@fedpolybida.edu.ng",
    phone: "07060942868",
    badge: "Assistant Lecturer",
  },
  {
    id: 14,
    title: "Engr.",
    name: "Mansur Ahmed",
    role: "Principal Instructor",
    rank: "Principal Instructor",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/mansur-ahmed.jpg`,
    qualifications: [
      "HND (Bida Poly)",
      "B.Eng (FUT Minna)",
      "M.Sc (UTP, Malaysia)",
    ],
    professionalQualifications: ["COREN", "MNSE", "MNSChE", "MNATE"],
    publications: "3 journals and 6 conference papers",
    researchInterests: [
      "CO2 Capture, Utilization and Storage (CCUS)",
      "Enhanced Oil Recovery (EOR)",
    ],
    specialties: [
      "B.Eng (Chemical Engineering)",
      "M.Sc (Petroleum Engineering)",
      "PhD in view (Petroleum Engineering)",
    ],
    awards: [],
    email: "mansur.ahmed@fedpolybida.edu.com",
    phone: "+2348033568046",
    badge: "Principal Instructor",
  },
  {
    id: 15,
    title: "Mallam",
    name: "Nma Aliyu Jipo",
    role: "Principal Instructor I",
    rank: "Principal Instructor I",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/nma-aliyu-jipo.jpeg`,
    qualifications: ["PGD (FUT Minna)"],
    professionalQualifications: ["NATE"],
    publications: "",
    researchInterests: [
      "Renewable Energy",
      "Shea Butter Production",
      "Petroleum Engineering",
      "Absorption",
    ],
    specialties: ["PGD (Chemical Engineering)"],
    awards: [],
    email: "nmajipo@fedpolybida.edu.ng",
    phone: "08065259893",
    badge: "Principal Instructor I",
  },
  {
    id: 16,
    title: "Technologist",
    name: "Rabiat Musa",
    role: "Senior Technologist",
    rank: "Senior Technologist",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/rabiat-musa.jpg`,
    qualifications: ["HND (Niger State Polytechnic Zungeru)"],
    professionalQualifications: ["NETE"],
    publications: "",
    researchInterests: ["Biochemical", "Analysis"],
    specialties: [],
    awards: [],
    email: "",
    phone: "08069683020",
    badge: "Senior Technologist",
  },
  {
    id: 17,
    title: "Technologist",
    name: "Usman Shaba Mohammed",
    role: "Technologist",
    rank: "Assistant Chef Technologies",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/mis.jpeg`,
    qualifications: ["HND (Federal Polytechnic Bida)"],
    professionalQualifications: ["NATE"],
    publications: "Nil",
    researchInterests: [
      "Production",
      "Food Processing",
      "Manufacturing",
      "Drying",
      "Extraction",
      "Designing",
      "Reaction Engineering",
      "Troubleshooting of Equipment",
      "Biotechnology",
      "Unit Operation",
    ],
    specialties: ["HND (Chemical Engineering)"],
    awards: [],
    email: "mohammed.usman@fedpolybida.edu",
    phone: "08068982742",
    badge: "Technologist",
  },
  {
    id: 18,
    title: "Engr.",
    name: "Abubakar Sadiq Umar",
    role: "Technologies II",
    rank: "Technologies II",
    image: `${CHEMICAL_STAFF_IMAGE_BASE}/abubakar-sadiq-umar.png`,
    qualifications: ["HND"],
    professionalQualifications: ["COREN"],
    publications: "",
    researchInterests: [
      "Chemical Engineering Technology",
      "Materials Science and Engineering",
      "Process Engineering and Industrial Technology",
    ],
    specialties: ["Chemical Engineering"],
    awards: [],
    email: "fedpolybida.edu.ng",
    phone: "09034277272",
    badge: "Technologies II",
  },
{
    id: 18,
    name: "Engr. Ismaila Isa Paiko",
    rank: "Senior Lecturer",
    image: "/images/departments/chemical-engineering/staff/ismaila-isa-paiko.jpg",
    shortBio: "Bsc, Msc (FUT Minna) • MNSE, COREN",
    email: "ismaila.paiko@fedpolybida.edu.ng",
    phone: "08030400759",
    qualifications: [
      "Bsc",
      "Msc (FUT Minna)"
    ],
    professionalMemberships: [
      "MNSE, COREN"
    ],
    publications: "25+",
    researchInterests: [
      "Energy Synthesis and Simulation",
      "Material Development",
      "Gas Transport Facility Design",
      "Heat Exchanger Network Synthesis Design",
      "Renewable Energy Efficiency and Design"
    ],
    specialization: "B. Eng. Chemical Engineering; M. Eng Chemical Engineering",
    achievements: [],
  },
  {
    id: 19,
    name: "Engr. Yakatun Alhaji Abdullahi",
    rank: "Chief Lecturer",
    image: "/images/departments/chemical-engineering/staff/yakatun-alhaji-abdullahi.jpg",
    shortBio: "ND ( FEDPOLY Bida), B.Eng • NATE, MNSChE, MNSE and  COREN",
    email: "Abdullahi.yakatun@fedpolbida.edu.ng",
    phone: "08066372440",
    qualifications: [
      "ND ( FEDPOLY Bida)",
      "B.Eng",
      "M.Eng. (FUT, Minna)"
    ],
    professionalMemberships: [
      "(NATE, MNSChE, MNSE and  COREN)"
    ],
    publications: "26",
    researchInterests: [
      "Renewable Energy",
      "Renewable Energy Storage Devices",
      "Environmental Engineering",
      "Bioenergy production",
      "Cement production",
      "Process and system Engineering",
      "Food science and technology",
      "Chemical Reaction Engineering",
      "Food Engineering",
      "Polymer and ceramic Engineering"
    ],
    specialization: "B.Eng (Chemical Engineering); M,Eng ( Chemical Engineering)",
    achievements: [
      "Development of a small scale soap production plant"
    ],
  },
  {
    id: 20,
    name: "Engr. Chidebelu Ugo Ibeme",
    rank: "Chief Lecturer",
    image: "/images/departments/chemical-engineering/staff/chidebelu-ugo-ibeme.jpg",
    shortBio: "B.Eng., M. Eng. UNIBEN • MNSChE, MNSE, COREN",
    email: "chidebelu.ibeme@fedpolybida.edu.ng",
    phone: "08082036107",
    qualifications: [
      "B.Eng.",
      "M. Eng. UNIBEN"
    ],
    professionalMemberships: [
      "MNSChE, MNSE, COREN"
    ],
    publications: "",
    researchInterests: [
      "Safety and Environmental Engineering",
      "Food processing technology"
    ],
    specialization: "Food processing technology",
    achievements: [],
  },
  {
    id: 21,
    name: "Engr. Ndagiman Lawal",
    rank: "Chief lecturer",
    image: "/images/departments/chemical-engineering/staff/ndagiman-lawal.jpg",
    shortBio: "B Engr M Engr FUT Minna • MNChE MNSE COREN",
    email: "ndagimanlawal@gmail.com",
    phone: "08039736481",
    qualifications: [
      "B Engr M Engr FUT Minna"
    ],
    professionalMemberships: [
      "MNChE MNSE COREN"
    ],
    publications: "20",
    researchInterests: [
      "Kinetic studies of thermal decomposition limestone",
      "Production of organic fertilizer using human urine banana steam ash and animals bone",
      "Determination of kinetics parameters for saponification reaction in batch rector",
      "Extraction and characterization of oil from Ginger",
      "Carbonization of cofired coal sawdust and simulation of pollution potential in production of Energy",
      "Characterization and kinetic studies of coal biomass blend co pyrolysis",
      "Comparative study of Amino Acid profile of some plants part used for making traditional soup",
      "Impact of waste Engine oil from selected mechanic site on surfaces water in Bida",
      "Optimization and Analysis of compressive strength of lDPE sandcrete composite using two factorial design",
      "Production and determination of phsiochemical properties of synthetic glue from waste polystyrene foam using organic solventn"
    ],
    specialization: "Chemical Engineering; Solid fuel Engineering",
    achievements: [],
  },
  {
    id: 22,
    name: "Engr. Umar Danlad Sule",
    rank: "Chief Lecturer",
    image: "/images/departments/chemical-engineering/staff/umar-danlad-sule.jpg",
    shortBio: "HND(IMT ENUGU), PGD(FUT MINNA) • MNSCHe, MNSE, COREN",
    email: "danladi@fedpolybida.edu.ng",
    phone: "08036572846",
    qualifications: [
      "HND(IMT ENUGU)",
      "PGD(FUT MINNA)",
      "M.ENG(FUT MINNA)"
    ],
    professionalMemberships: [
      "MNSCHe, MNSE, COREN"
    ],
    publications: "20",
    researchInterests: [
      "CATALYSIS",
      "FOOD TECHNOLOGY",
      "WASTE MANAGEMENT",
      "RENEWABLE ENERGY",
      "DESIGN AND DEVELOPMENT",
      "PETROLEUM",
      "BIOCHEMICAL",
      "POLLUTION",
      "CORROSION",
      "POLYMER"
    ],
    specialization: "HND (CHEMICAL ENGINEERING) PGD (CHEMICAL ENGINEERING); M.ENG (CATALYSIS)",
    achievements: [],
  }
];

/* =========================================================
   WHY STUDY HERE
========================================================= */

const studyReasons = [
  {
    icon: Beaker,
    title: "Practical, Hands-on Training",
    description:
      "Students combine theory with laboratory experiments, workshops, industrial training and practical engineering projects.",
  },
  {
    icon: Wrench,
    title: "Exposure to Modern Technology",
    description:
      "Students develop skills in process control, industrial automation, computer applications, laboratory analysis, environmental protection and modern manufacturing.",
  },
  {
    icon: Factory,
    title: "Industrial Experience",
    description:
      "Polytechnic education provides a strong foundation for industrial attachment and workplace experience in real production environments.",
  },
  {
    icon: GraduationCap,
    title: "Pathway to Further Education",
    description:
      "ND and HND qualifications can provide a foundation for further academic and professional development, subject to applicable requirements.",
  },
  {
    icon: Sparkles,
    title: "Foundation for Innovation",
    description:
      "Students are encouraged to develop innovative solutions and products from locally available materials, creating commercial and employment opportunities.",
  },
];

/* =========================================================
   CAREER / INDUSTRY PROSPECTS
========================================================= */

const industryProspects = [
  "Oil and Gas",
  "Petrochemicals",
  "Pharmaceuticals",
  "Food and Beverage",
  "Cement and Building Materials",
  "Water and Wastewater Treatment",
  "Paints and Chemicals",
  "Plastics and Polymers",
  "Fertilizer Production",
  "Renewable Energy",
  "Environmental Management",
  "Lecturing in Higher Institutions of Learning",
];

const selfEmploymentProspects = [
  "Soap and Detergent Production",
  "Water Treatment Services",
  "Paint Production",
  "Cosmetics Production",
  "Food Processing",
  "Plastics and Chemical-Based Products",
  "Local Raw-Material Processing",
  "Process Improvement and Production Support",
];

/* =========================================================
   CERTIFICATIONS
========================================================= */

const certifications = [
  "Aspen Plus / Aspen HYSYS Process Simulation",
  "NEBOSH International General Certificate",
  "HAZOP / HAZID and Process Safety",
  "ISO 9001 Lead Auditor",
  "ISO 14001 Environmental Management",
  "ISO 45001 Occupational Health & Safety",
  "Project Management Professional (PMP)",
  "Energy Management / ISO 50001",
  "Python or MATLAB for Engineering",
  "Environmental Impact Assessment",
  "Oil & Gas / Petrochemical Process Engineering",
  "Artificial Intelligence and Data Analytics for Process Engineering",
];

/* =========================================================
   COURSE STRUCTURE

   The uploaded source labels both lists as "Major Course Structure (ND)".
   The second list is therefore presented as "Additional Major Courses"
   until the department confirms whether it is intended to be HND.
========================================================= */

const ndCourses = [
  "Introduction to Chemical Engineering I & II",
  "Chemical Engineering Plant Services & Maintenance",
  "Chemical Engineering Drawing",
  "Introduction to Engineering Analysis",
  "Chemical Engineering Thermodynamics",
  "Unit Operation",
  "Mass Transfer",
  "Heat Transfer",
  "Fluid Mechanics",
  "Corrosion and Material Science",
  "Project Seminar",
  "SIWES",
  "Engineering Management",
  "Chemical Reaction Engineering I",
  "Instrumentation & Process Control",
  "Health, Safety and Environment",
  "Chemical Engineering Laboratory",
  "Biotechnology",
  "Polymer Science and Technology",
  "Fuel Technology",
  "Petroleum Processing Technology",
];

const additionalCourses = [
  "Chemical Engineering Laboratory",
  "Heat Transfer",
  "Unit Operations",
  "Chemical Engineering Thermodynamics",
  "Mass Transfer",
  "Chemical Engineering Process Calculation",
  "Strength of Material",
  "Fluid Mechanics",
  "Chemical Reaction Engineering",
  "Polymer Science & Technology",
  "Computer Aided Design",
  "Chemical Plant Economics",
  "Food Science & Technology",
  "Chemical Engineering Analysis",
  "Engineering Management",
  "Pilot Plant & Scale-up",
  "Pulp & Paper Technology",
  "Process Metallurgy",
  "Equipment Design",
  "Chemical Process Dynamics & Control",
  "Health, Safety & Environment",
  "Chemical Engineering Entrepreneurship",
  "Plant Services & Maintenance",
  "Petroleum Refining & Petrochemical Technology",
  "Biochemical Engineering",
  "Gas Processing Technology",
  "Reservoir Engineering",
  "Research and Design Project",
];

/* =========================================================
   GALLERY

   Add department photographs under:
   public/images/departments/chemical-engineering/gallery/
   The gallery falls back to the department hero banner if a photo is missing.
========================================================= */

const galleryImages = [
  {
    img: "departments/chemical-engineering/gallery/ch3.jpg",
  },
  {
    img: "departments/chemical-engineering/gallery/chemical-gallery2.jpg",
    title: "Laboratory Process Experiment",
    caption: "Representative laboratory work involving chemical processing, controlled heating and practical experimentation.",
  },
  {
    img: "departments/chemical-engineering/gallery/chemical-gallery3.jpg",
    title: "Chemical Analysis Laboratory",
    caption: "Representative laboratory equipment used for separation, measurement and analytical chemical-engineering practice.",
  },
  {
    img: "departments/chemical-engineering/gallery/chemical-gallery4.jpg",
    title: "Pilot-Scale Process Equipment",
    caption: "Representative process equipment illustrating pilot-scale operations and hands-on engineering practice.",
  },
  {
    img: "departments/chemical-engineering/gallery/ch1.jpg",
  },
  {
    img: "departments/chemical-engineering/gallery/ch2.jpg",
  },
  {
    img: "departments/chemical-engineering/gallery/ch4.png",
  },
  {
    img: "departments/chemical-engineering/gallery/ch5.png",
  },
  {
    img: "departments/chemical-engineering/gallery/ch6.jpeg",
  },
  {
    img: "departments/chemical-engineering/gallery/ch7.jpeg",
  },
  {
    img: "departments/chemical-engineering/gallery/ch8.jpeg",
  },
  {
    img: "departments/chemical-engineering/gallery/ch9.jpeg",
  },
];

/* =========================================================
   STAFF ACADEMIC PROFILE MODAL
========================================================= */

const StaffAcademicProfileModal = ({ staff, onClose }) => {
  if (!staff) return null;

  const fullName = `${staff.title || ""} ${staff.name}`.trim();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/75 backdrop-blur-sm p-3 sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 18 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chemical-staff-profile-title"
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl md:rounded-3xl bg-white shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close staff academic profile"
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
        >
          <X size={21} />
        </button>

        <div className="grid md:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="relative overflow-hidden bg-gradient-to-b from-blue-950 via-blue-900 to-blue-800 p-7 pt-16 text-white md:p-8">
            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-cyan-300/10 blur-3xl" />

            <div className="relative z-10">
              <div className="mx-auto aspect-[4/5] w-full max-w-[280px] overflow-hidden rounded-2xl border-4 border-white/20 bg-slate-200 shadow-2xl">
                <img
                  src={staff.image}
                  alt={fullName}
                  className="h-full w-full object-cover object-top"
                  onError={(event) => {
                    event.currentTarget.src = "/images/default-staff.jpg";
                  }}
                />
              </div>

              <div className="mt-5 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-300/30 bg-white/10 px-4 py-2 text-xs font-semibold text-blue-50 backdrop-blur">
                  <BadgeCheck size={16} />
                  {staff.badge || staff.rank}
                </span>
              </div>

              <div className="mt-6 text-center md:text-left">
                <h2 className="text-2xl font-extrabold leading-tight text-white">
                  {fullName}
                </h2>
                <p className="mt-2 text-sm font-medium text-blue-200">{staff.role}</p>
              </div>

              {staff.email && (
                <a
                  href={`mailto:${staff.email}`}
                  className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10 hover:text-white"
                >
                  <Mail size={18} className="shrink-0 text-blue-300" />
                  <span className="break-all">{staff.email}</span>
                </a>
              )}

              {staff.phone && (
                <a
                  href={`tel:${staff.phone.replace(/\s+/g, "")}`}
                  className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10 hover:text-white"
                >
                  <Phone size={18} className="shrink-0 text-blue-300" />
                  <span>{staff.phone}</span>
                </a>
              )}
            </div>
          </aside>

          <div className="min-w-0 p-6 sm:p-8 md:p-10 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Department Staff
            </p>
            <h3
              id="chemical-staff-profile-title"
              className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl"
            >
              Staff Academic Profile
            </h3>
            <div className="mt-4 h-1 w-16 rounded-full bg-blue-700" />

            {staff.qualifications?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <GraduationCap size={20} />
                  </span>
                  Academic Qualifications
                </h4>
                <div className="mt-4 space-y-3">
                  {staff.qualifications.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700"
                    >
                      <CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {staff.professionalQualifications?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <ShieldCheck size={19} />
                  </span>
                  Professional Qualifications
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {staff.professionalQualifications.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {staff.publications && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <BookOpen size={19} />
                  </span>
                  Academic Publications
                </h4>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                  {staff.publications}
                </div>
              </section>
            )}

            {staff.researchInterests?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <BookOpenCheck size={19} />
                  </span>
                  Research Interests
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {staff.researchInterests.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {staff.specialties?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <FlaskConical size={19} />
                  </span>
                  Areas of Specialty
                </h4>
                <div className="mt-4 space-y-3">
                  {staff.specialties.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-slate-200 px-4 py-3 text-sm leading-6 text-slate-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {staff.awards?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                    <Award size={19} />
                  </span>
                  Academic Awards / Breakthroughs
                </h4>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {staff.awards.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/70 p-4"
                    >
                      <Award size={18} className="mt-0.5 shrink-0 text-amber-600" />
                      <span className="text-sm font-medium leading-6 text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   GALLERY LIGHTBOX
========================================================= */

const GalleryLightbox = ({ image, images, onClose, onChange }) => {
  if (!image) return null;

  const currentIndex = image.index;

  const showPrevious = () => {
    const nextIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onChange({ ...images[nextIndex], index: nextIndex });
  };

  const showNext = () => {
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onChange({ ...images[nextIndex], index: nextIndex });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/95 p-3 backdrop-blur-md sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.25 }}
        role="dialog"
        aria-modal="true"
        aria-label="Chemical Engineering gallery viewer"
        className="relative w-full max-w-6xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="absolute right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-800 shadow-xl transition hover:bg-blue-50 hover:text-blue-700"
        >
          <X size={22} />
        </button>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={showPrevious}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl transition hover:text-blue-700 md:left-5"
            >
              <ChevronLeft size={27} />
            </button>
            <button
              type="button"
              onClick={showNext}
              aria-label="Next image"
              className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl transition hover:text-blue-700 md:right-5"
            >
              <ChevronRight size={27} />
            </button>
          </>
        )}

        <div className="overflow-hidden rounded-2xl bg-black shadow-2xl">
          <div className="flex min-h-[260px] items-center justify-center bg-black">
            <motion.img
              key={image.img}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              src={`/images/${image.img}`}
              alt={image.title}
              className="max-h-[78vh] w-full object-contain"
              onError={(event) => {
                const fallback = `${CHEMICAL_IMAGE_BASE}/chemical-engineering-hero.jpg`;
                if (!event.currentTarget.src.endsWith("chemical-engineering-hero.jpg")) {
                  event.currentTarget.src = fallback;
                }
              }}
            />
          </div>

          <div className="flex flex-col gap-3 bg-slate-900 px-5 py-4 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold">{image.title}</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-300">{image.caption}</p>
              <p className="mt-1 text-xs text-slate-500">
                Image {currentIndex + 1} of {images.length}
              </p>
            </div>
            <p className="text-xs text-slate-400 sm:text-right">
              Department of Chemical Engineering
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const ChemicalEngineering = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);

  // Keep content visible by default so full-page screenshots and browsers
  // that delay IntersectionObserver do not produce blank sections.
  const fadeUp = {
    hidden: { opacity: 1, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    const overlayOpen = selectedStaff || selectedGalleryImage;
    if (!overlayOpen) return undefined;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedStaff(null);
        setSelectedGalleryImage(null);
        return;
      }

      if (selectedGalleryImage && event.key === "ArrowLeft") {
        const currentIndex = selectedGalleryImage.index;
        const nextIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }

      if (selectedGalleryImage && event.key === "ArrowRight") {
        const currentIndex = selectedGalleryImage.index;
        const nextIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedStaff, selectedGalleryImage]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {/* =====================================================
          DEPARTMENT NAVIGATION
      ===================================================== */}
      <nav className="sticky top-0 z-50 flex items-center gap-6 overflow-x-auto whitespace-nowrap bg-white/90 px-4 py-3 text-sm shadow-md backdrop-blur-md md:px-20">
        <div className="flex-1 font-semibold text-blue-700">
          Chemical Engineering — Federal Polytechnic Bida
        </div>

        <div className="flex gap-4">
          <a href="#brief" className="hover:text-blue-500">Overview</a>
          <a href="#why" className="hover:text-blue-500">Why Study</a>
          <a href="#admission" className="hover:text-blue-500">Admission</a>
          <a href="#prospects" className="hover:text-blue-500">Careers</a>
          <a href="#certifications" className="hover:text-blue-500">Certifications</a>
          <a href="#staff" className="hover:text-blue-500">Meet Staff</a>
          <a href="#courses" className="hover:text-blue-500">Course Structure</a>
          <a href="#brochure" className="hover:text-blue-500">Brochure</a>
          <a href="#gallery" className="hover:text-blue-500">Gallery</a>
          <a href="#contact" className="hover:text-blue-500">Contact</a>
        </div>
      </nav>

      {/* =====================================================
          HERO
      ===================================================== */}
      <header className="relative flex h-[340px] items-center justify-center overflow-hidden md:h-[420px]">
        <img
          src={`${CHEMICAL_IMAGE_BASE}/chemical-engineering-hero.jpg`}
          alt="Department of Chemical Engineering"
          className="absolute inset-0 h-full w-full scale-105 object-cover"
          onError={(event) => {
            const fallback = "/images/department-hero.jpg";
            if (!event.currentTarget.src.endsWith(fallback)) {
              event.currentTarget.src = fallback;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-blue-950/70 to-slate-950/45" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.28),transparent_35%)]" />

        {/* Chemical Engineering visual icons */}
        <motion.div
          aria-hidden="true"
          className="absolute left-[7%] top-10 hidden h-16 w-16 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-blue-100 backdrop-blur-md md:flex"
          animate={{ y: [0, -8, 0], rotate: [0, 3, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <FlaskConical size={30} />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute right-[10%] top-14 hidden h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-blue-100 backdrop-blur-md md:flex"
          animate={{ y: [0, 7, 0], rotate: [0, -4, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Factory size={27} />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute bottom-12 right-[20%] hidden h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-blue-100 backdrop-blur-md lg:flex"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Beaker size={24} />
        </motion.div>

        <div className="relative z-10 px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-blue-50 backdrop-blur-md"
          >
            <FlaskConical size={17} className="text-blue-200" />
            Chemical Engineering Technology
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="text-3xl font-extrabold md:text-5xl"
          >
            Department of Chemical Engineering
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-3 max-w-3xl"
          >
            Practical engineering education, applied research, sustainable process development and industry-ready skills.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mx-auto mt-5 flex max-w-3xl flex-wrap justify-center gap-2.5"
          >
            {[
              { icon: Factory, label: "Process Engineering" },
              { icon: Beaker, label: "Laboratory Practice" },
              { icon: ShieldCheck, label: "Process Safety" },
              { icon: Leaf, label: "Sustainable Engineering" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/25 px-3 py-1.5 text-xs font-medium text-blue-50 backdrop-blur-sm sm:text-sm"
                >
                  <Icon size={15} className="text-blue-200" />
                  {item.label}
                </span>
              );
            })}
          </motion.div>

          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href="/admissions"
              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 text-white shadow transition hover:bg-blue-600"
            >
              Apply Now
            </a>

            <a
              href="/chemical-engineering-brochure.pdf"
              download
              className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 text-blue-700 shadow"
            >
              <Download size={19} />
              Brochure (PDF)
            </a>
          </div>
        </div>
      </header>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}
      <main className="space-y-12 px-6 py-12 md:px-20">
        {/* DEPARTMENT BRIEF */}
        <motion.section
          id="brief"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <BookOpen className="text-blue-700" />
            The Department in Brief
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            The Department of Chemical Engineering was established in 1977 and commenced effectively in the 1977/78 Academic Session. Over the years, the Department has had about ten Heads of Department. The present Head of Department, Engr. Dr. Muhammad Adamu, joined the Department in July 2006.
          </p>

          <p className="mt-4 text-justify leading-relaxed text-gray-700">
            The Department initially offered both National Diploma (ND) and Higher National Diploma (HND) programmes until 1988 when the HND programme was phased out due to inadequate laboratory facilities. In 1994, NBTE granted permission for the Department to remount the HND programme after equipment was received through the World Technical Education Scheme Project, and the programmes were re-accredited in 1996.
          </p>

          <p className="mt-4 text-justify leading-relaxed text-gray-700">
            Many ND and HND graduates have passed through the Department and are gainfully employed in industries across the country. The Department continues to prepare students for industrial practice, further education, innovation and professional development in Chemical Engineering Technology.
          </p>
        </motion.section>

        {/* HOD SPEECH */}
        <motion.section
          id="hodspeech"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="relative flex flex-col items-start gap-10 overflow-hidden rounded-2xl bg-white p-8 shadow-lg md:flex-row"
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.06),transparent_70%)]" />

          <div className="relative z-10 flex w-full justify-center md:w-1/3">
            <motion.img
              src={`${CHEMICAL_STAFF_IMAGE_BASE}/muhammad-adamu.jpg`}
              alt="Engr. Dr. Muhammad Adamu, Head of Department"
              className="h-52 w-52 rounded-full border-4 border-white object-cover object-top shadow-xl"
              whileHover={{ rotate: 2, scale: 1.04 }}
              transition={{ type: "spring", stiffness: 200, damping: 12 }}
              onError={(event) => {
                event.currentTarget.src = "/images/default-staff.jpg";
              }}
            />
          </div>

          <div className="relative z-10 flex-1">
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <Users className="text-blue-700" />
              HOD Speech
            </h2>
            <Divider />

            <div className="mb-6 flex flex-wrap gap-3">
              {[
                "PhD Chemical Engineering",
                "Chief Lecturer",
                "MNSE",
                "COREN",
                "31 Journal Publications",
                "Research & Fabrication Grants",
              ].map((badge) => (
                <span
                  key={badge}
                  className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 shadow"
                >
                  {badge}
                </span>
              ))}
            </div>

            <div className="mb-6 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 shadow-sm">
              <p className="font-medium italic text-blue-900">
                “Our commitment is to produce competent, innovative, ethical, and industry-ready chemical engineering technologists through practical education, applied research, and sustainable engineering practice.”
              </p>
            </div>

            <p className="text-justify leading-relaxed text-gray-700">
              It gives me great pleasure, on behalf of the staff and students of the Department of Chemical Engineering, to warmly welcome you to our department. Choosing a Polytechnic programme is one of the most important decisions you will make in shaping your future. If you are passionate about science, technology, innovation, problem-solving and creating solutions that can improve lives and transform society, then Chemical Engineering Technology is a field worth considering.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Chemical Engineering Technology is much more than the study of chemicals. It is a broad and dynamic engineering discipline concerned with the transformation of raw materials into useful products and services through the application of chemistry, physics, mathematics, biology and engineering principles. Chemical engineers play important roles in oil and gas, petrochemicals, pharmaceuticals, food and beverages, agriculture, energy, water and wastewater treatment, manufacturing, biotechnology, materials development and environmental management.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              Our students combine theory with practical learning through laboratory experiments, engineering projects, design activities and industrial training. The Department also encourages innovation and research in renewable energy, sustainable processes, environmental protection, biotechnology, advanced materials, process automation, digital technologies and green manufacturing.
            </p>

            <p className="mt-6 font-semibold text-blue-700">
              — Engr. Dr. Muhammad Adamu, MNSE, COREN
            </p>
            <p className="text-gray-600">Head of Department</p>
          </div>
        </motion.section>

        {/* WHY STUDY HERE */}
        <motion.section
          id="why"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-900 p-8 text-white shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Award className="text-yellow-300" />
            Why Study Here
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-100">
            Chemical Engineering is one of the most versatile and industry-relevant engineering disciplines. Studying it in the Polytechnic provides students with practical skills, technical knowledge and entrepreneurial opportunities needed to contribute to Nigeria’s industrial development.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {studyReasons.map((item) => {
              const Icon = item.icon;
              return (
              <div key={item.title} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/10">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-800 shadow-sm">
                  <Icon size={19} />
                </span>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-blue-100">{item.description}</p>
                </div>
              </div>
              );
            })}
          </div>
        </motion.section>

        {/* ADMISSION */}
        <motion.section
          id="admission"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Users className="text-blue-700" />
            Admission Requirements
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            Candidates applying for admission to study Chemical Engineering Technology, either at National Diploma (ND) or Higher National Diploma (HND) level, are required to have a minimum of five credits in O’Level at not more than two sittings with Mathematics, English Language, Physics, Chemistry and any other science subject.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-5">
              <h3 className="font-bold text-blue-900">National Diploma (ND)</h3>
              <p className="mt-2 text-sm leading-6 text-gray-700">
                Applicants must satisfy the O’Level requirement in addition to the prescribed JAMB (UTME) score for the admission year.
              </p>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-5">
              <h3 className="font-bold text-blue-900">Higher National Diploma (HND)</h3>
              <p className="mt-2 text-sm leading-6 text-gray-700">
                Applicants must satisfy the O’Level requirement and must have obtained the relevant National Diploma certificate.
              </p>
            </div>
          </div>
        </motion.section>

        <Divider delay={0.1} />

        {/* JOB PROSPECTS */}
        <motion.section
          id="prospects"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Factory className="text-blue-700" />
            Job Prospects
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            Chemical Engineering Technology graduates can work across major production, process, energy, environmental and manufacturing sectors. The discipline also provides opportunities for self-employment and enterprise development.
          </p>

          <div className="mt-6 grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-blue-700">
                <BriefcaseBusiness size={19} />
                Industry Opportunities
              </h3>
              <ul className="grid gap-2 text-gray-700 sm:grid-cols-2">
                {industryProspects.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-1 shrink-0 text-blue-700" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-blue-700">
                <Sparkles size={19} />
                Self-Employment & Enterprise
              </h3>
              <ul className="space-y-2 text-gray-700">
                {selfEmploymentProspects.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-1 shrink-0 text-blue-700" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                Nigeria needs technically skilled graduates who can transform raw materials into finished and value-added products, improve production processes, reduce waste, control costs and support local industrial development.
              </div>
            </div>
          </div>
        </motion.section>

        {/* CERTIFICATIONS */}
        <motion.section
          id="certifications"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-50 p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <ShieldCheck className="text-blue-700" />
            Certification / Professional Courses
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-800">
            Professional certifications and continuing professional development can strengthen a Chemical Engineering CV and complement academic qualifications. Depending on career direction, development through NSE, COREN-related programmes, safety bodies, universities, oil-and-gas training institutions and internationally recognised providers can be valuable.
          </p>

          <div className="mt-6 grid gap-x-8 gap-y-3 md:grid-cols-2">
            {certifications.map((item) => (
              <div key={item} className="flex items-start gap-3 text-gray-700">
                <CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />
                <span>{item}</span>
              </div>
            ))}
          </div>
      
          <p className="text-justify leading-relaxed text-gray-800">
            In Nigeria,
            For a Nigerian Chemical Engineer, I would also distinguish between professional qualifications that have strong international recognition and local professional development courses. For example, depending on your career direction, professional development through NSE, COREN-related continuing professional development, safety bodies, universities, oil-and-gas training institutions, and internationally recognized certification providers can complement your academic qualification.
          </p>
        </motion.section>

        {/* MEET OUR STAFF */}
        <motion.section
          id="staff"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <div>
            <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900">
              <Users2 className="text-blue-700" />
              Meet Our Staff
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 sm:text-base">
              Meet the academic and technical professionals contributing to teaching, applied research, laboratory practice, innovation and student development in the Department of Chemical Engineering.
            </p>
          </div>
          <Divider />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {staffList.map((staff) => {
              const fullName = `${staff.title || ""} ${staff.name}`.trim();
              return (
                <motion.button
                  type="button"
                  key={staff.id}
                  onClick={() => setSelectedStaff(staff)}
                  whileHover={{ y: -7 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  aria-label={`View academic profile of ${fullName}`}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all duration-300 hover:border-blue-200 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <div className="relative h-[310px] overflow-hidden bg-slate-100">
                    <img
                      src={staff.image}
                      alt={fullName}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(event) => {
                        event.currentTarget.src = "/images/default-staff.jpg";
                      }}
                    />

                    <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-slate-950 via-slate-950/65 to-transparent" />

                    {staff.featured && (
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg">
                        <BadgeCheck size={14} />
                        HOD
                      </span>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                      <h3 className="text-lg font-bold leading-tight">{fullName}</h3>
                      <p className="mt-1.5 text-sm font-medium text-blue-200">{staff.role}</p>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex min-h-[42px] items-start gap-2.5 text-sm text-slate-600">
                      <FlaskConical size={17} className="mt-0.5 shrink-0 text-blue-700" />
                      <span className="line-clamp-2 leading-5">
                        {staff.researchInterests?.[0] || staff.specialties?.[0] || staff.rank}
                      </span>
                    </div>

                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-sm font-bold text-blue-700">
                      <span>View Academic Profile</span>
                      <ChevronRight size={19} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Click or tap any staff card to view the staff academic profile.
          </p>
        </motion.section>

        {/* COURSE STRUCTURE */}
        <motion.section
          id="courses"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <GraduationCap className="text-blue-700" />
            Course Structure
          </h2>
          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg bg-gray-50 p-5">
              <h3 className="mb-3 text-lg font-semibold text-slate-900">
                Major Course Structure (ND)
              </h3>
              <ol className="list-inside list-decimal space-y-1.5 text-sm text-gray-700">
                {ndCourses.map((course, index) => (
                  <li key={`${course}-${index}`}>{course}</li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-gray-50 p-5">
              <h3 className="mb-3 text-lg font-semibold text-slate-900">
                Major Courses Structure (HND)
              </h3>
              
              <ol className="list-inside list-decimal space-y-1.5 text-sm text-gray-700">
                {additionalCourses.map((course, index) => (
                  <li key={`${course}-${index}`}>{course}</li>
                ))}
              </ol>
            </div>
          </div>
        </motion.section>

        {/* BROCHURE */}
        <motion.section
          id="brochure"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-blue-900 p-6 text-white shadow-lg"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <FileText className="text-white" />
              Admission Brochure
            </h2>

            <a
              href="/chemical-engineering-brochure.pdf"
              download
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-blue-900"
            >
              <Download size={18} />
              Download PDF
            </a>
          </div>
          <Divider />

          <div className="grid gap-4 md:grid-cols-3">
            {[
              {
                title: "Programme Overview",
                text: "Explore Chemical Engineering Technology, its industrial relevance and programme opportunities.",
                icon: Beaker,
              },
              {
                title: "Admission Pathway",
                text: "Review ND and HND entry requirements and the academic pathway into the Department.",
                icon: GraduationCap,
              },
              {
                title: "Professional Development",
                text: "Discover certifications, process-simulation tools, safety training and professional development opportunities.",
                icon: Wrench,
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-xl bg-white/10 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">{item.text}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* GALLERY */}
        <motion.section
          id="gallery"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <div>
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <FlaskConical className="text-blue-700" />
              Department Gallery
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
              Explore photographs from laboratory activities, engineering projects, student learning, facilities and departmental events.
            </p>
          </div>
          <Divider />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {galleryImages.map((image, index) => (
              <motion.button
                type="button"
                key={image.img}
                onClick={() => setSelectedGalleryImage({ ...image, index })}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                aria-label={`View gallery image ${index + 1}`}
                className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left shadow-sm transition hover:border-blue-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={`/images/${image.img}`}
                    alt={image.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(event) => {
                      const fallback = `${CHEMICAL_IMAGE_BASE}/chemical-engineering-hero.jpg`;
                      if (!event.currentTarget.src.endsWith("chemical-engineering-hero.jpg")) {
                        event.currentTarget.src = fallback;
                      }
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/5 to-transparent" />

                  <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-blue-700 shadow-lg transition group-hover:scale-110">
                    <Maximize2 size={17} />
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="font-semibold">{image.title}</p>
                    <p className="mt-1 text-xs text-slate-200">Click to View</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.section>

        {/* CONTACT + MAP */}
        <motion.section
          id="contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <MapPin className="text-blue-700" />
            Contact the Department
          </h2>
          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-gray-700">
                <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Mail className="text-blue-700" size={19} />
                chemical.engineering@fedpolybida.edu.ng
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Phone className="text-blue-700" size={19} />
                +234 806 529 5882
              </p>

              <div className="mt-6 rounded-xl bg-blue-50 p-5">
                <h3 className="flex items-center gap-2 font-semibold text-blue-900">
                  <Leaf size={18} />
                  Engineering for Sustainable Development
                </h3>
                <p className="mt-2 text-sm leading-6 text-blue-800">
                  The Department promotes practical education, applied research, sustainable process development, entrepreneurship and the transformation of local raw materials into value-added products.
                </p>
              </div>
            </div>

            <div>
              <iframe
                title="Federal Polytechnic Bida location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                width="100%"
                height="260"
                loading="lazy"
                className="rounded-md border-0 shadow-sm"
              />
            </div>
          </div>
        </motion.section>
      </main>

      {/* STAFF ACADEMIC PROFILE */}
      <AnimatePresence>
        {selectedStaff && (
          <StaffAcademicProfileModal
            key={`staff-${selectedStaff.id}`}
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}
      </AnimatePresence>

      {/* GALLERY LIGHTBOX */}
      <AnimatePresence>
        {selectedGalleryImage && (
          <GalleryLightbox
            key="chemical-engineering-gallery-lightbox"
            image={selectedGalleryImage}
            images={galleryImages}
            onClose={() => setSelectedGalleryImage(null)}
            onChange={setSelectedGalleryImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChemicalEngineering;
